import { format, parseISO } from "date-fns";

/*
 * Who reviewed a result, and when (KIN-1819 / KIN-2122).
 *
 * A result could read "Reviewed" with no way to tell who had reviewed it, because
 * nothing recorded it. The list endpoint now carries `reviewedById`, `reviewedBy` (a
 * display name, title + first + last) and `reviewedAt` on every reviewed row — and
 * clears all three when a result is put back to pending.
 *
 * Results reviewed before that change still read CONFIRMED with no reviewer, and there
 * is no backfill, so "reviewed by nobody" has to be a state this can describe.
 */

const REVIEWED_STATUS = "CONFIRMED";

/*
 * A string is what the results endpoint sends. The object shape is what documents
 * record a reviewer as, and the two surfaces are close enough that a row from one is
 * read by code written for the other sooner or later.
 */
const toReviewerName = (reviewedBy) => {
  if (typeof reviewedBy === "string") return reviewedBy.trim();

  if (!reviewedBy) return "";

  return `${reviewedBy.firstName || ""} ${reviewedBy.lastName || ""}`.trim();
};

// An ISO local date-time. The time is kept: two people reviewing the same patient's
// results on the same day is an ordinary morning, and the date alone can't separate them.
const formatReviewedAt = (reviewedAt) => {
  if (!reviewedAt) return "";

  try {
    const parsed = parseISO(reviewedAt);

    return Number.isNaN(parsed.getTime()) ? "" : format(parsed, "dd-MM-yyyy HH:mm");
  } catch {
    return "";
  }
};

/**
 * What the status line should say about a result's review.
 *
 * `label` is the whole sentence, so the panels keep rendering one value.
 */
export const getResultReviewStatus = (item) => {
  const isReviewed = item?.orderStatus === REVIEWED_STATUS;

  if (!isReviewed) {
    return { isReviewed: false, reviewerName: "", reviewedAt: "", label: "Pending Review" };
  }

  const reviewerName = toReviewerName(item?.reviewedBy);
  const reviewedAt = formatReviewedAt(item?.reviewedAt);

  /*
   * Reviewed before KIN-1819, or by a staff member who can no longer be found. Said
   * plainly rather than left blank: blank reads as "nobody reviewed this", and nothing
   * in this app reviews a result automatically.
   */
  if (!reviewerName) {
    return {
      isReviewed: true,
      reviewerName: "",
      reviewedAt,
      label: "Reviewed (reviewer not recorded)",
    };
  }

  return {
    isReviewed: true,
    reviewerName,
    reviewedAt,
    label: reviewedAt
      ? `Reviewed by ${reviewerName} on ${reviewedAt}`
      : `Reviewed by ${reviewerName}`,
  };
};
